// src/hooks/useDentallyBooking.js
import { useState } from 'react';
import { callApi } from '../utils/apiCall';
import { createDentallyAppointmentAsync, createDentallyPatient, exportLeadToDentally } from '../utils/dentallyApi';

const extractApiError = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  // DRF standard: { detail: "..." }
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  // Generic message/error keys
  if (typeof data.message === 'string') return data.message;
  if (typeof data.error === 'string') return data.error;
  // Dentally API errors: { error: { type, message, documentation_url } }
  if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
    return data.error.message;
  }
  // DRF field errors: { field: ["msg", ...] } or { field: "msg" }
  const pairs = Object.entries(data);
  const strEntry = pairs.find(([, v]) => typeof v === 'string');
  if (strEntry) return `${strEntry[0]}: ${strEntry[1]}`;
  const arrEntry = pairs.find(([, v]) => Array.isArray(v) && v.length > 0);
  if (arrEntry) {
    const msg = typeof arrEntry[1][0] === 'string' ? arrEntry[1][0] : JSON.stringify(arrEntry[1][0]);
    return `${arrEntry[0]}: ${msg}`;
  }
  const objEntry = pairs.find(([, v]) => v && typeof v === 'object' && !Array.isArray(v));
  if (objEntry) {
    const nested = extractApiError(objEntry[1], null);
    if (nested) return `${objEntry[0]}: ${nested}`;
  }
  // Last resort: truncated raw JSON (useful during dev)
  try {
    const str = JSON.stringify(data);
    return str.length > 300 ? `${str.slice(0, 300)}…` : str;
  } catch (jsonErr) {
    console.warn('[extractApiError] JSON.stringify failed', jsonErr);
    return fallback || 'An error occurred (unable to parse details).';
  }
};

/**
 * Manages the sequential API calls for Dentally appointment booking.
 *
 * Normal mode (dentallyOnly=false):
 *   Steps: (1) create lead → (2) add treatment → (3) CRM appointment → (4) Dentally appointment → (5) payment link
 *   existingLead: lead object if booking from lead detail page, null if new lead
 *
 * Dentally-only test mode (dentallyOnly=true):
 *   Steps: (1) create Dentally patient → (2) create Dentally appointment
 *   No CRM lead, treatment, or appointment is created.
 */
export function useDentallyBooking({ practiceId, existingLead = null, dentallyOnly = false, existingTreatmentId = null, isPaymentRequired = false }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: true, steps: [...], leadId, ... }
  const [error, setError] = useState(null);   // { step, message }
  const [bookingStep, setBookingStep] = useState(null); // { index: 1-5, label: string }

  const reset = () => { setResult(null); setError(null); setBookingStep(null); };

  /**
   * formData shape:
   * {
   *   // Patient (new lead only)
   *   firstName, lastName, phone, email,
   *   salutation,            // 'Mr' | 'Mrs' | 'Miss' | 'Ms' | 'Dr' — required by Dentally
   *   dob,                   // 'YYYY-MM-DD' string — required by Dentally
   *   postcode,              // string — required by Dentally
   *   // Treatment
   *   treatmentId,           // practice treatment ID
   *   // Appointment
   *   practitionerId,        // Dentally practitioner ID (number)
   *   reason,                // string from DENTALLY_REASON_OPTIONS
   *   dateTime,              // JS Date — used as CRM datetime AND Dentally start_time
   *   durationMinutes,       // number (15/30/45/60)
   *   method,                // 'IN_PRACTICE' | 'VIDEO_CALL' | 'PHONE'
   *   assignees,             // array of practice user IDs
   *   notes,                 // string (optional)
   *   // Payment
   *   paymentOption,         // 'none' | 'received' | 'link'
   *   paymentAmount,         // number in pounds e.g. 50.00 — required if 'received' or 'link'
   *   paymentType,           // 'DEPOSIT' | 'CONSULTATION' | 'TREATMENT' — required if not 'none'
   *   receivedMode,          // 'PHONE' | 'INPRACTICE' | 'BANK_TRANSFER' | 'OTHER' — only if paymentOption === 'received'
   *   paymentLinkChannel,    // 'email' | 'whatsapp' | 'both' — delivery channel for payment link (default: 'email')
   * }
   */
  const submit = async (formData) => {
    if (!practiceId) {
      setError({ step: 0, message: 'Practice not loaded. Please try again.' });
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    const steps = [];  // Tracks which steps succeeded for the success screen
    const isDeferred = isPaymentRequired && formData.paymentOption === 'link';

    try {
      // ══════════════════════════════════════════════════════════════════
      // Dentally-only path — no CRM lead, treatment, or appointment
      // ══════════════════════════════════════════════════════════════════
      if (dentallyOnly) {
        // Step 1: Create patient directly in Dentally
        const pRes = await createDentallyPatient(practiceId, {
          title: formData.salutation,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email_address: formData.email,
          mobile_phone: formData.phone,
          date_of_birth: formData.dob,
          // Dentally requires gender as boolean: true=male, false=female
          gender: formData.sex === 'Male',
          // Dentally requires ethnicity — default to "99" (Patient declined), never shown in UI
          ethnicity: '99',
          address_attributes: {
            address_line_1: formData.addressLine1,
            postcode: formData.postcode,
          },
        });
        if (!pRes.ok) {
          setError({ step: 1, message: extractApiError(pRes.data, 'Failed to create patient in Dentally.') });
          return;
        }
        const dentallyPatientId = pRes.data?.id || pRes.data?.patient?.id || pRes.data?.patient_id;
        if (!dentallyPatientId) {
          setError({ step: 1, message: 'Patient created but no Dentally patient ID returned.' });
          return;
        }
        steps.push('dentally_patient_created');

        // Step 2: Create Dentally appointment
        const startIso = formData.dateTime instanceof Date
          ? formData.dateTime.toISOString()
          : formData.dateTime;
        const finishTime = new Date(new Date(startIso).getTime() + formData.durationMinutes * 60000).toISOString();
        const dRes = await createDentallyAppointmentAsync(practiceId, {
          dentally_patient_id: String(dentallyPatientId),
          start_time: startIso,
          finish_time: finishTime,
          practitioner_id: formData.practitionerId,
          reason: formData.reason,
          ...(formData.notes ? { notes: formData.notes } : {}),
        });
        if (!dRes.ok) {
          setError({ step: 2, message: extractApiError(dRes.data, 'Patient created but Dentally appointment failed.') });
          return;
        }
        steps.push('dentally_appointment_created');

        setResult({
          success: true,
          leadId: null,
          steps,
          dentallyWarning: null,
          paymentWarning: null,
          startTime: startIso,
          finishTime,
          practitionerId: formData.practitionerId,
          reason: formData.reason,
          dentallyPatientId,
        });
        return;
      }

      // ── Step 1: Create lead (new lead only) ────────────────────────────
      let leadId = existingLead != null ? existingLead.id : null;
      if (!existingLead) {
        setBookingStep({ index: 1, label: 'Creating lead…' });
        const r = await callApi(`/api/v1/practices/${practiceId}/leads/`, 'post', {
          first_name: formData.firstName,
          last_name: formData.lastName,
          salutation: formData.salutation?.toUpperCase(),
          dob: formData.dob,
          sex: formData.sex,
          lead_contact: { phone: formData.phone, email: formData.email },
          lead_address: { postcode: formData.postcode, address_line_1: formData.addressLine1 },
          ...(formData.nhs_number ? { nhs_number: formData.nhs_number } : {}),
          ...(formData.emergencyContactName ? { emergency_contact_name: formData.emergencyContactName } : {}),
          ...(formData.emergencyContactPhone ? { emergency_contact_phone: formData.emergencyContactPhone } : {}),
        });
        if (!r.ok) {
          console.error('[DentallyBooking] Step 1 – create lead failed', r.status, r.data);
          setError({ step: 1, message: extractApiError(r.data, 'Failed to create lead.') });
          return;
        }
        leadId = r.data.id;
        steps.push('lead_created');
      }

      if (leadId == null) {
        setError({ step: 1, message: 'Lead ID is missing. Cannot continue.' });
        return;
      }

      // ── Step 2: Add treatment ───────────────────────────────────────────
      setBookingStep({ index: 2, label: 'Adding treatment…' });
      let treatmentRecordId;
      if (existingTreatmentId) {
        treatmentRecordId = existingTreatmentId;
        steps.push('treatment_created');
      } else if (formData.treatmentName) {
        const tRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`, 'post', {
          treatment: formData.treatmentName,
          status: 'NEW',
          // Suppress "new lead" email notification — the lead is being booked straight into consultation
          is_practice_notification_required: false,
        });
        if (!tRes.ok) {
          console.error('[DentallyBooking] Step 2 – add treatment failed', tRes.status, tRes.data);
          setError({ step: 2, message: extractApiError(tRes.data, 'Failed to add treatment.') });
          return;
        }
        treatmentRecordId = tRes.data.id;
        steps.push('treatment_created');
      }

      // Guard: if no treatment could be resolved, abort before creating appointments or payments
      if (!treatmentRecordId && !existingTreatmentId) {
        console.error('[DentallyBooking] Step 2 – no treatment record could be created or found');
        setError({ step: 2, message: 'A treatment is required to complete the booking. Please select or create a treatment and try again.' });
        return;
      }
      const startIso = formData.dateTime instanceof Date
        ? formData.dateTime.toISOString()
        : formData.dateTime;

      if (!startIso) {
        setError({ step: 3, message: 'Appointment date and time is required.' });
        return;
      }

      if (!isDeferred) {
        // ── Step 3: CRM appointment ─────────────────────────────────────────
        setBookingStep({ index: 3, label: 'Creating appointment…' });
        const aRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/`, 'post', {
          datetime: startIso,
          method: formData.method,
          type: 'CONSULTATION',
          contacted_via: 'PHONE', // how the booking was taken — always phone for Dentally bookings
          lead_treatment: treatmentRecordId,
          assignees: formData.assignees || [],
        });
        if (!aRes.ok) {
          console.error('[DentallyBooking] Step 3 – CRM appointment failed', aRes.status, aRes.data);
          setError({ step: 3, message: extractApiError(aRes.data, 'Failed to create CRM appointment.') });
          return;
        }
        steps.push('crm_appointment_created');
      }

      let dentallyWarning = null;
      const finishTime = new Date(
        new Date(startIso).getTime() + (formData.durationMinutes || 30) * 60000
      ).toISOString();
      let dentallyPayload = null;
      if (!isDeferred) {
        // ── Step 4: Dentally appointment ────────────────────────────────────
        setBookingStep({ index: 4, label: 'Booking in Dentally…' });

        // If existing lead was missing required Dentally fields and the form collected them,
        // PATCH the CRM lead record first so the backend export has the correct data.
        if (existingLead && leadId) {
          const missingFields = {};
          if (formData.salutation && !existingLead.salutation) missingFields.salutation = formData.salutation;
          if (formData.sex && !existingLead.sex) missingFields.sex = formData.sex;
          if (formData.dob && !existingLead.dob) missingFields.dob = formData.dob;
          if (formData.nhs_number && !existingLead.nhs_number) missingFields.nhs_number = formData.nhs_number;
          if (formData.emergencyContactName && !existingLead.emergency_contact_name) missingFields.emergency_contact_name = formData.emergencyContactName;
          if (formData.emergencyContactPhone && !existingLead.emergency_contact_phone) missingFields.emergency_contact_phone = formData.emergencyContactPhone;
          if (Object.keys(missingFields).length > 0) {
            try {
              await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', missingFields);
            } catch (patchErr) {
              console.warn('[DentallyBooking] Failed to patch missing lead fields before export', patchErr);
            }
          }
        }

        // Export to Dentally to ensure a Dentally patient record exists (new and existing leads)
        if (leadId) {
          try {
            await exportLeadToDentally(leadId);
          } catch (exportErr) {
            console.warn('[DentallyBooking] Pre-export failed (non-blocking)', exportErr);
          }
        }

        const dentallyExtraFields = {
          ...(formData.salutation && !existingLead?.salutation ? { patient_title: formData.salutation } : {}),
          ...(formData.sex && !existingLead?.sex ? { patient_sex: formData.sex } : {}),
          ...(formData.dob && !existingLead?.dob ? { patient_dob: formData.dob } : {}),
        };
        const dRes = await createDentallyAppointmentAsync(practiceId, {
          lead_id: leadId,
          start_time: startIso,
          finish_time: finishTime,
          practitioner_id: formData.practitionerId,
          reason: formData.reason,
          ...(formData.notes ? { notes: formData.notes } : {}),
          ...dentallyExtraFields,
        });
        dentallyPayload = {
          lead_id: leadId,
          start_time: startIso,
          finish_time: finishTime,
          practitioner_id: formData.practitionerId,
          reason: formData.reason,
          ...(formData.notes ? { notes: formData.notes } : {}),
          ...dentallyExtraFields,
        };
        if (!dRes.ok) {
          // CRM appointment is already saved — do NOT block; show amber warning
          console.error('[DentallyBooking] Step 4 – Dentally appointment failed', dRes.status, dRes.data);
          dentallyWarning = extractApiError(dRes.data, 'Could not sync appointment to Dentally.');
          // Set pending_dentally_sync metadata so retry is available on the lead detail page
          try {
            const metaRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
            const existing = Array.isArray(metaRes?.data?.lead_metadata) ? metaRes.data.lead_metadata : [];
            const merged = [...existing];
            const idx = merged.findIndex((m) => m.key === 'pending_dentally_sync');
            if (idx !== -1) merged[idx] = { ...merged[idx], value: 'yes' };
            else merged.push({ key: 'pending_dentally_sync', value: 'yes' });
            await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', { lead_metadata: merged });
          } catch (metaErr) {
            console.warn('[DentallyBooking] Failed to set pending_dentally_sync metadata', metaErr);
          }
        } else {
          steps.push('dentally_appointment_created');
        }
      }

      // ── Step 5: Payment (optional) ──────────────────────────────────────
      let paymentWarning = null;
      let paymentRecordId = null;
      let checkoutUrl = null;
      if (formData.paymentOption === 'received' || formData.paymentOption === 'link') {
        setBookingStep({ index: 5, label: 'Processing payment…' });
        // Step 5a: Create LeadTreatmentPayment record
        const linkChannel = formData.paymentLinkChannel || 'email';
        // Always use 'EMAIL' for mode_of_payment — WhatsApp is handled client-side via wa.me deep link
        const linkModeOfPayment = 'EMAIL';
        const paymentBody = {
          payment_type: formData.paymentType || 'DEPOSIT',
          mode_of_payment: formData.paymentOption === 'received'
            ? (formData.receivedMode || 'PHONE')
            : linkModeOfPayment,
          amount: formData.paymentAmount,
          ...(formData.paymentOption === 'received'
            ? { paid_at: new Date().toISOString() }
            : {}),
        };
        const p5aRes = await callApi(
          `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentRecordId}/payments/`,
          'post',
          paymentBody,
        );
        if (!p5aRes.ok) {
          console.error('[DentallyBooking] Step 5a – payment record failed', p5aRes.status, p5aRes.data);
          paymentWarning = extractApiError(p5aRes.data, 'Failed to record payment.');
        } else if (formData.paymentOption === 'link') {
          // Step 5b: Create Stripe checkout session (sends the payment link by email)
          const paymentId = p5aRes.data.id;
          paymentRecordId = paymentId;
          const p5bRes = await callApi(
            `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentRecordId}/payments/${paymentId}/checkout_sessions/`,
            'post',
            {},
          );
          if (!p5bRes.ok) {
            console.error('[DentallyBooking] Step 5b – checkout session failed', p5bRes.status, p5bRes.data);
            paymentWarning = extractApiError(p5bRes.data, 'Payment recorded but link failed to send.');
          } else {
            steps.push('payment_link_sent');
            checkoutUrl = p5bRes.data?.url || null;

            // Auto-open WhatsApp if channel is 'whatsapp' or 'both'
            if (checkoutUrl && (linkChannel === 'whatsapp' || linkChannel === 'both')) {
              const rawPhone = formData.phone || '';
              let waPhone = rawPhone.replace(/[\s-]/g, '');
              if (waPhone.startsWith('0')) waPhone = `44${waPhone.slice(1)}`;
              if (waPhone.startsWith('+')) waPhone = waPhone.slice(1);
              if (waPhone) {
                const waMsg = encodeURIComponent(`Here is your secure payment link:\n${checkoutUrl}\n\nPlease complete the payment at your convenience.`);
                const waWindow = window.open(`https://wa.me/${waPhone}?text=${waMsg}`, '_blank');
                if (waWindow) {
                  steps.push('whatsapp_link_sent');
                } else {
                  paymentWarning = paymentWarning || 'WhatsApp could not be opened (popup blocked). Please send the payment link manually.';
                }
              } else {
                paymentWarning = paymentWarning || 'No phone number available for WhatsApp. Please send the payment link manually.';
              }
            } else if (!checkoutUrl && (linkChannel === 'whatsapp' || linkChannel === 'both')) {
              paymentWarning = paymentWarning || 'Payment link URL not available. Please send the link manually from the payment tab.';
            }

            // ── Step 5c: Persist pending booking data (deferred mode only) ──────────
            if (isDeferred) {
              try {
                const pendingData = {
                  practitionerId: formData.practitionerId,
                  dateTime: startIso,
                  durationMinutes: formData.durationMinutes,
                  reason: formData.reason,
                  method: formData.method,
                  assignees: formData.assignees || [],
                  notes: formData.notes || '',
                  treatmentName: formData.treatmentName || '',
                  treatmentRecordId,
                  paymentRecordId: paymentId,
                  paymentAmount: formData.paymentAmount || null,
                  savedAt: new Date().toISOString(),
                };
                const metaRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
                const existing = Array.isArray(metaRes?.data?.lead_metadata) ? metaRes.data.lead_metadata : [];
                const merged = [...existing];
                const idx = merged.findIndex((m) => m.key === 'dentally_pending_booking');
                const entry = { key: 'dentally_pending_booking', value: JSON.stringify(pendingData) };
                if (idx !== -1) merged[idx] = entry;
                else merged.push(entry);
                await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', { lead_metadata: merged });
                steps.push('dentally_booking_deferred');
              } catch (metaErr) {
                console.warn('[DentallyBooking] Failed to write dentally_pending_booking metadata', metaErr);
              }
            }

            // ── Step 5d: Auto-create reminder task (deferred mode only) ─────────────
            if (isDeferred) {
              try {
                const datetime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
                const amount = formData.paymentAmount ? `£${formData.paymentAmount}` : '';
                await callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', {
                  method: 'PAYMENT',
                  datetime,
                  note: `[DENTALLY_BOOKING_PENDING] Dentally payment pending${amount ? ` (${amount})` : ''} — lead is still NEW. Once patient pays, go to lead overview to complete the Dentally booking and move to Consultation Booked.`,
                  assignees: formData.assignees || [],
                  is_completed: false,
                  group: 'FOLLOW_UP',
                  lead: leadId,
                });
                steps.push('dentally_reminder_created');
              } catch (reminderErr) {
                console.warn('[DentallyBooking] Failed to create deferred reminder', reminderErr);
              }
            }

          }
        } else {
          steps.push('payment_received');
        }
      }

      const leadName = existingLead
        ? [existingLead.first_name, existingLead.last_name].filter(Boolean).join(' ')
        : [formData.firstName, formData.lastName].filter(Boolean).join(' ');

      setResult({
        success: true,
        leadId,
        leadName: leadName || null,
        treatmentRecordId,
        paymentRecordId,
        checkoutUrl,
        contactEmail: formData.email || null,
        contactPhone: formData.phone || null,
        paymentLinkChannel: formData.paymentLinkChannel || 'email',
        paymentAmount: formData.paymentAmount || null,
        steps,
        isDeferred,
        dentallyWarning,
        dentallyPayload: dentallyWarning ? dentallyPayload : null,
        paymentWarning,
        startTime: startIso,
        finishTime,
        practitionerId: formData.practitionerId,
        reason: formData.reason,
      });
    } catch (err) {
      console.error('[DentallyBooking] Unexpected error at step', bookingStep?.index, err);
      const stepLabel = bookingStep?.label || 'booking';
      setError({ step: bookingStep?.index || 0, message: `Booking failed during "${stepLabel}". Please try again. (${err?.message || 'Unknown error'})` });
    } finally {
      setLoading(false);
    }
  };

  return { loading, result, error, bookingStep, submit, reset };
}
